package ai.wagemix.logic

import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.firebase.WMUserStatus

/** The site's routes, as the presenters name them in navigation requests. */
object WMRoutes {
    const val HOME = "/"
    const val PRICING = "/pricing"
    const val LOGIN = "/login"
    const val REGISTER = "/register"
    const val FORGOT_PASSWORD = "/forgot-password"
    const val AUTH_ACTION = "/auth-action"
    const val MIX = "/mix"
    const val MIXES = "/mixes"
    const val SHARED_LINKS = "/shared-links"
    const val JOB_DESCRIPTIONS = "/job-descriptions"
    const val ACCOUNT = "/account"
    const val PAYMENT_RESULT = "/payment-result"
    const val PRIVACY_POLICY = "/privacy-policy"

    fun mix(mixId: String) = "$MIX/$mixId"
    fun sharedMix(sharedMixId: String) = "/s/mix/$sharedMixId"
}

/** A navigation the presenter decided on; the page maps it onto the router. */
sealed interface WMNavigationRequest {
    /** An in-site route. */
    data class Route(val path: String): WMNavigationRequest
    /** A full-page redirect to another origin (Stripe Checkout, the billing portal). */
    data class External(val url: String): WMNavigationRequest
}

/**
 * The plan rules the presenters share. Free (no subscription) sees locked weights and filters
 * and cannot upload; Basic unlocks them with three job-description uploads; Pro is unlimited.
 * Each rule takes the plan the user may use now (`entitledPlanOf`, `none` once lapsed), never the
 * stored `subscriptionType` alone, which a lapsed account keeps so it knows what to renew.
 */
object WMPlanRules {
    const val BASIC_UPLOAD_QUOTA = 3

    fun canAdjustWeights(plan: WMSubscriptionType?) = plan != null && plan != WMSubscriptionType.None
    fun canFilter(plan: WMSubscriptionType?) = plan != null && plan != WMSubscriptionType.None
    fun canSave(plan: WMSubscriptionType?) = plan != null && plan != WMSubscriptionType.None
    fun canShare(plan: WMSubscriptionType?) = plan != null && plan != WMSubscriptionType.None

    /**
     * A lapsed account: it had a subscription that ended (the webhook sets `status` to `inactive` and keeps the plan
     * it lapsed from). It sees the renewal reminder rather than the upgrade prompts a never-paid account sees, and none
     * of its paid data until it renews (planning/paid-feature-enforcement-plan.md D2, W7). `past_due` stays `active`,
     * so a retried payment is not lapsed.
     */
    fun isLapsed(status: WMUserStatus?): Boolean = status == WMUserStatus.Inactive

    /** `null` means unlimited; `0` means the plan has no uploads. */
    fun uploadQuota(plan: WMSubscriptionType?): Int? = when (plan) {
        WMSubscriptionType.Pro -> null
        WMSubscriptionType.Basic -> BASIC_UPLOAD_QUOTA
        WMSubscriptionType.None, null -> 0
    }

    /**
     * Where a freshly signed-in (verified) user lands; the rules the current site applies after
     * login and registration.
     */
    fun postLoginRoute(plan: WMSubscriptionType, status: WMUserStatus, workflow: String): String = when (plan) {
        WMSubscriptionType.Basic, WMSubscriptionType.Pro -> when (status) {
            WMUserStatus.Active -> WMRoutes.MIX
            WMUserStatus.Created -> "${WMRoutes.MIX}?workflow=$workflow"
            WMUserStatus.Inactive -> "${WMRoutes.PRICING}?inactive=true&workflow=$workflow"
        }
        WMSubscriptionType.None -> when (status) {
            WMUserStatus.Active, WMUserStatus.Created -> "${WMRoutes.PRICING}?workflow=$workflow"
            WMUserStatus.Inactive -> "${WMRoutes.PRICING}?inactive=true&workflow=$workflow"
        }
    }
}
